import React, { useRef, useState } from "react";
import { Checkbox, FormControlLabel, FormGroup } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import TourManagementSidebar from "../components/tour/TourManagementSidebar";
import { Paths } from "../router/Paths";
import useOnLoadPage from "../hooks/useOnLoadPage";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../app/store";
import {
  setImagesOwnership,
  setPackageData,
  setPackageGallery,
} from "../reducer/tourReducer";
import { usePackage } from "../hooks/usePackage";
import { addActiveTab } from "../reducer/sideBarSlice";
import { AxiosResponse } from "axios";
import { postRequest } from "../api/commonActions/post";
import { Requests } from "../config/api";
import { resetTourEdit } from "../reducer/tourEditReducer";
import { putRequest } from "../api/commonActions/update";

// todo: fix the upload issue when editing the images
function TourManagementPackagePhotoUploadPage() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [validationError, setValidationError] = useState("");
  const [continueLoading, setContinueLoading] = useState(false);
  const { packageData, gallery, PrepackageDataId } = useSelector(
    (state: RootState) => state.tour
  );
  const { tourEdit, id } = useSelector((state: RootState) => state.edit);
  const [prePackageImages, setPrePackageImages] = useState<any[]>([]);

  const imageLink = packageData?.gallery;

  const [images, setImages] = useState<any[]>(imageLink || gallery || []);
  const { createPrePackage } = usePackage();
  const progressref = useRef(10);

  const MAX_IMAGES = 5;
  const MAX_SIZE = 7 * 1024 * 1024;

  useOnLoadPage({ tabActive: "create" });

  const [imagesOwnership, setImagesOwnershipState] = useState(
    (packageData?.gallery?.length ?? 0) > 0
  );

  const handleCheckboxChange = () => {
    const newOwnershipState = !imagesOwnership;
    setImagesOwnershipState(newOwnershipState);
    dispatch(setImagesOwnership(newOwnershipState));
  };

  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault();
  };

  const handleDrop = (event: React.DragEvent) => {
    if (!imagesOwnership) return;
    event.preventDefault();
    const files = event.dataTransfer.files;
    handleImageUpload(files);
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    handleImageUpload(files);
  };

  // Convert File to base64
  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        resolve(reader.result as string);
      };
      reader.onerror = (error) => reject(error);
      reader.readAsDataURL(file);
    });
  };

  // Upload image and convert to base64 with validation
  const handleImageUpload = async (files: FileList | null) => {
    if (!files) return;

    // Check if the number of images exceeds the max limit
    if (images.length + files.length > MAX_IMAGES) {
      alert(`You can only upload up to ${MAX_IMAGES} images.`);
      return;
    }

    const validImageFiles = Array.from(files).filter((file) => {
      // Check file type
      const validTypes = ["image/jpeg", "image/png", "image/gif"];
      if (!validTypes.includes(file.type)) {
        alert(
          `Invalid file type: ${file.type}. Only JPG, JPEG, PNG, and GIF are allowed.`
        );
        return false;
      }

      // Check file size
      if (file.size > MAX_SIZE) {
        alert(`File is too large: ${file.name}. Maximum size is 7MB.`);
        return false;
      }

      return true;
    });

    const updatedPrePackageImages = [...prePackageImages, ...validImageFiles];
    setPrePackageImages(updatedPrePackageImages);

    // Convert valid images to base64
    const base64Images = await Promise.all(
      validImageFiles.map((file) => fileToBase64(file))
    );

    const updatedImages = [...images, ...base64Images];
    setImages(updatedImages);
    dispatch(setPackageGallery(updatedImages));
  };

  const handleRemoveImage = (index: number) => {
    const updatedImages = images.filter((_, idx) => idx !== index);
    setImages(updatedImages);
    dispatch(setPackageGallery(updatedImages));
  };

  const savePackage = async (setLoadingState: (value: boolean) => void) => {
    const formData = new FormData();

    prePackageImages.forEach((file) => {
      formData.append("gallery", file);
    });

    // Append id only if it exists
    if (PrepackageDataId) {
      formData.append("_id", PrepackageDataId);
    }

    formData.append("progress", progressref.current.toString());

    formData.append("title", packageData.title ?? "");
    formData.append("referenceCode", packageData.referenceCode ?? "");
    formData.append("shortDescription", packageData.shortDescription ?? "");
    formData.append("fullDescription", packageData.fullDescription ?? "");

    packageData.highlights?.forEach((item) =>
      formData.append("highlights[]", item)
    );
    packageData.locations?.forEach((item) =>
      formData.append("locations[]", item)
    );
    packageData.keywords?.forEach((item) =>
      formData.append("keywords[]", item)
    );
    packageData.inclusions?.forEach((item) =>
      formData.append("inclusions[]", item)
    );
    packageData.exclusions?.forEach((item) =>
      formData.append("exclusions[]", item)
    );

    formData.append("guideInformations", packageData.guideInformations ?? "");
    formData.append("foodIncluded", String(packageData.foodIncluded ?? ""));
    formData.append("transportation", String(packageData.transportation ?? ""));
    formData.append(
      "customerCityTravel",
      String(packageData.customerCityTravel ?? "")
    );

    packageData.mealSettings?.forEach((item) => {
      const obj = typeof item === "string" ? JSON.parse(item) : item;
      formData.append("mealSettings[]", JSON.stringify(obj));
    });

    packageData.modeOfTransport?.forEach((item) =>
      formData.append("modeOfTransport[]", item)
    );
    packageData.notSuitableFor?.forEach((item) =>
      formData.append("notSuitableFor[]", item)
    );
    packageData.allowedList?.forEach((item) =>
      formData.append("allowedList[]", item)
    );
    packageData.prohibitedList?.forEach((item) =>
      formData.append("prohibitedList[]", item)
    );
    packageData.mandatoryItems?.forEach((item) =>
      formData.append("mandatoryItems[]", item)
    );

    formData.append("contactMethod", packageData.contactMethod ?? "");
    formData.append(
      "informationForCustomers",
      packageData.informationForCustomers ?? ""
    );
    formData.append("city", packageData.city ?? "");
    formData.append("country", packageData.country ?? "");

    setLoadingState(true);

    try {
      if (tourEdit) {
        const response: AxiosResponse = await putRequest({
          route: `${Requests.update.updatePackage}${id}`,
          payload: formData,
        });

        if (response?.status === 200) {
          dispatch(resetTourEdit());
          dispatch(setPackageData(packageData));
          return { success: true, id, fromEdit: true };
        } else {
          console.error(
            "Error updating package:",
            response?.data?.message || "Unknown error"
          );
          return { success: false };
        }
      } else {
        const response: AxiosResponse = await postRequest({
          route: Requests.post.createPrePackage,
          payload: formData,
        });

        if (response?.status === 201) {
          return { success: true };
        } else {
          console.error(
            "Error saving package:",
            response?.data?.message || "Unknown error"
          );
          return { success: false };
        }
      }
    } catch (error) {
      console.error("Error saving package:", error);
      return { success: false };
    } finally {
      setLoadingState(false);
    }
  };

  const MIN_IMAGES = 4;

  const validateImages = (): boolean => {
    if (images.length < MIN_IMAGES) {
      setValidationError(
        `Please upload at least ${MIN_IMAGES} photos to continue.`
      );
      return false;
    }
    setValidationError("");
    return true;
  };

  const handleContinue = async () => {
    if (!validateImages()) return;
    // local loading state for continue
    const result = await savePackage(setContinueLoading);
    if (result.success) {
      dispatch(addActiveTab(Paths.tourManagementPackageOptions));
      navigate(Paths.tourManagementPackageOptions);
    }
  };

  const onSave = async () => {
    if (!validateImages()) return;

    const result = await savePackage(setLoading);
    if (result.success) {
      navigate(Paths.welcome);
    }
  };

  return (
    <div className={Classname.container}>
      <Navbar />
      <div className={Classname.content}>
        <TourManagementSidebar selected={11} />
        <div className={Classname.detail}>
          <div className={Classname.title}>
            Drag your photos into the area below or select "Upload". To change
            the order, click and drag.
          </div>
          <div className="flex items-center my-2">
            <FormGroup onChange={handleCheckboxChange}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={imagesOwnership}
                    onChange={handleCheckboxChange}
                  />
                }
                label={
                  <label className="text-sm text-gray-400">
                    I own the copyright for these pictures, and have model
                    release forms for any recognizable faces. I understand I’m
                    liable for any copyright infringement
                  </label>
                }
              />
            </FormGroup>
          </div>
          <div>
            Your photos should have no more than 7MB each and be at least 1280px
            wide. Also, please use only JPG, JPEG, PNG, or GIF images.
          </div>
          {/* <div className="mt-4">
            <label
              htmlFor="uploadInput"
              className={`${Classname.continueButton} ${
                imagesOwnership ? "opacity-100" : "opacity-30"
              }`}
            >
              Upload
              <input
                type="file"
                accept="image/*"
                onChange={handleFileUpload}
                className="hidden"
                id="uploadInput"
                multiple
                disabled={!imagesOwnership}
              />
            </label>
          </div> */}
          <div
            className={`flex flex-col flex-wrap gap-4 justify-center mt-6 mb-4 border-2 py-8 ${
              imagesOwnership ? "opacity-100" : "opacity-30"
            }`}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
          >
            <div className="flex flex-col lg:flex-row lg:justify-center gap-2 items-center mb-4">
              Drop your photos here <span> or</span>
              <span>
                <label
                  htmlFor="uploadInput"
                  className="text-primary1 cursor-pointer"
                >
                  click to upload.
                  <input
                    id="uploadInput"
                    type="file"
                    accept="image/*"
                    onChange={handleFileUpload}
                    className="hidden"
                    multiple
                    disabled={!imagesOwnership}
                  />
                </label>
              </span>
            </div>
            <div className="px-4 lg:px-6 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {images.length > 0 &&
                images.map((image, index) => (
                  <div key={index} className="w-full">
                    <div className="relative w-full aspect-square">
                      <img
                        src={image}
                        className="w-full h-full rounded-md object-cover"
                        alt={`Image ${index + 1}`}
                      />
                      <button
                        className={Classname.imageDeleteButton}
                        onClick={() => handleRemoveImage(index)}
                      >
                        <img
                          src="/IconClose1.svg"
                          alt=""
                          className={Classname.closeIcon}
                        />
                      </button>
                    </div>
                  </div>
                ))}
            </div>
          </div>

          {validationError && (
            <p className="text-red-500 text-sm mt-2">{validationError}</p>
          )}

          <div className="flex gap-4 justify-between lg:justify-end mt-4">
            <button
              className={Classname.saveButton}
              onClick={onSave}
              disabled={loading}
            >
              {loading ? "Please Wait..." : "Save and exit"}
            </button>
            {!tourEdit && (
              <button
                disabled={continueLoading}
                className={Classname.continueButton}
                onClick={handleContinue}
              >
                {continueLoading ? "Please Wait..." : "Continue"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default TourManagementPackagePhotoUploadPage;

const Classname = {
  container:
    "flex w-full min-h-screen relative bg-white flex-col items-center font-main",
  content:
    "flex flex-col lg:flex-row w-full max-w-7xl flex-1 pt-10 lg:pt-0 max-lg:px-4",
  detail: "flex flex-col py-10 lg:px-6 w-full",
  title: "font-bold text-lg mt-6 mb-2",
  titleOptional: "text-sm text-gray-500 font-normal",
  input: "flex w-full h-14 border border-gray-400 rounded-md px-4 mt-2",
  textarea:
    "flex w-full h-44 py-4 border border-gray-400 rounded-md px-4 mt-2 mb-6",
  description: "flex text-base mb-4",
  buttonsContainer: "flex flex-1 justify-end gap-3 mt-10",
  highlightButton:
    "font-bold flex items-center gap-2 cursor-pointer text-primary1 py-4",
  saveButton:
    "h-12 px-6 flex items-center justify-center font-bold text-gray-700 border border-gray-500 rounded-md",
  continueButton:
    "h-12 w-fit px-6 flex items-center justify-center text-white font-bold bg-primary1 rounded-md cursor-pointer",
  closeIcon: "w-4 object-contain",
  imageDeleteButton:
    "bg-white w-6 h-6 rounded-full flex items-center justify-center absolute right-2 top-2 shadow-md cursor-pointer",
};
